import { getActiveAdventure } from "../adventure/ActiveAdventure.js";
import { dataRegistry } from "../loaders/DataRegistry.js";
import type { AbilityName } from "../types/entity.js";
import type { ClassData } from "./classData.js";

/**
 * EV-E7 — the character creator's roster, resolved from the adventure
 * manifest's `roster` field. Returns null when the manifest declares no
 * roster, in which case the creator keeps its classic hardcoded lists —
 * existing adventures need no changes.
 */

export interface CreatorRoster {
  readonly classes: readonly ClassData[];
  readonly races: readonly { id: string; label: string }[];
  readonly backgrounds: readonly { id: string; label: string }[];
}

export async function loadCreatorRoster(): Promise<CreatorRoster | null> {
  const roster = getActiveAdventure().manifest.roster;
  if (!roster) return null;

  const classes: ClassData[] = [];
  for (const id of roster.classes) {
    const rec = await dataRegistry.getClass(id);
    if (!rec) {
      console.warn(`[rosterLoader] class "${id}" in manifest roster has no record — skipped`);
      continue;
    }
    const l1 = rec.spellcasting?.slots_per_level[0];
    const spellSlots: Record<number, number> = {};
    if (l1) l1.forEach((n, i) => n > 0 && (spellSlots[i + 1] = n));
    classes.push({
      id: rec.index,
      label: rec.name,
      maxHp: rec.chargen?.max_hp ?? rec.hit_die + 2,
      ac: rec.chargen?.ac ?? 12,
      ...(Object.keys(spellSlots).length > 0 ? { spellSlots } : {}),
      ...(rec.spellcasting?.ability
        ? { castingAbility: rec.spellcasting.ability as AbilityName }
        : {}),
    });
  }

  const races: { id: string; label: string }[] = [];
  for (const id of roster.races) {
    const rec = await dataRegistry.getRace(id);
    if (rec) races.push({ id: rec.index, label: rec.name });
    else console.warn(`[rosterLoader] race "${id}" in manifest roster has no record — skipped`);
  }

  const backgrounds: { id: string; label: string }[] = [];
  for (const id of roster.backgrounds) {
    const rec = await dataRegistry.getBackground(id);
    if (rec) backgrounds.push({ id: rec.index, label: rec.name });
    else
      console.warn(`[rosterLoader] background "${id}" in manifest roster has no record — skipped`);
  }

  if (classes.length === 0 || races.length === 0 || backgrounds.length === 0) return null;
  return { classes, races, backgrounds };
}
